import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { COMMA, ENTER, SEMICOLON, SPACE } from '@angular/cdk/keycodes';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NEVER, Observable, of, Subscription } from 'rxjs';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption
} from '@angular/material/autocomplete';
import { FormTutorial, Tutorial, TutorialSaved, TutorialType } from '../../models/tutorial';
import { HowToService } from '../../services/how-to.service';
import { StringHelperService } from '../../../../services/string-helper.service';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { DragAndDropDirective } from '../../../../directives/drag-and-drop.directive';
import { MatButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

interface DialogData {
  editMode: boolean;
  tutorial?: Tutorial;
}

@Component({
  selector: 'app-new-tutorial',
  templateUrl: './new-tutorial.component.html',
  styleUrls: ['./new-tutorial.component.scss'],
  imports: [
    MatButton,
    MatRadioGroup,
    MatRadioButton,
    ReactiveFormsModule,
    MatLabel,
    MatFormField,
    MatInput,
    DragAndDropDirective,
    MatTooltip,
    MatIcon,
    MatAutocompleteTrigger,
    MatChipInput,
    MatAutocomplete,
    MatOption,
    AsyncPipe,
    MatChipGrid,
    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    MatChipRow,
    MatSuffix,
    MatChipRemove,
    MatProgressSpinner
  ],
  standalone: true
})
export class NewTutorialComponent implements OnInit, OnDestroy {
  @ViewChild('tagInput', { static: false }) tagInput!: ElementRef<HTMLInputElement>;
  @ViewChild('auto', { static: false }) matAutocomplete!: MatAutocomplete;
  form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(100)] }),
    description: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(1000)]
    }),
    internalsOnly: new FormControl(true, { nonNullable: true, validators: Validators.required }),
    tags: new FormControl<string[]>([], { nonNullable: true, validators: Validators.required }),
    type: new FormControl<TutorialType>('Video', { nonNullable: true, validators: Validators.required }),
    url: new FormControl('', { nonNullable: true, validators: Validators.required })
  });
  separatorKeysCodes = [ENTER, COMMA, SPACE, SEMICOLON];
  fileName = '';
  file: File | null = null;
  isSaving = false;
  fileUploadTouched = false;
  filteredTags$: Observable<string[]> = of([]);
  tagCtrl = new FormControl();
  thumbnailUploadTouched = false;
  thumbnail: File | null = null;
  thumbnailName = '';

  private subscription = new Subscription();
  private readonly fileMaxSize = 26214400;

  constructor(
    private tutorialsService: HowToService,
    private dialogRef: MatDialogRef<NewTutorialComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private snackBar: MatSnackBar,
    private stringHelperService: StringHelperService
  ) {}

  ngOnInit() {
    this.filteredTags$ = this.tutorialsService.getTags().pipe(
      switchMap(allTags => {
        return this.tagCtrl.valueChanges.pipe(
          startWith(null),
          map((selectedTags: string) => (selectedTags ? this.filterTags(allTags, selectedTags) : allTags))
        );
      })
    );

    this.handleTypeChange();
    this.setFormValues();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  add({ chipInput, value }: MatChipInputEvent) {
    if (!this.matAutocomplete.isOpen) {
      const tagsControl = this.form.get('tags');

      if (value && this.isTagNameValid(value.trim())) {
        tagsControl?.setValue([...new Set([...(tagsControl?.value || []), value.trim()])]);
        tagsControl?.updateValueAndValidity();
      }

      chipInput.clear();
    }
  }

  selected({ option }: MatAutocompleteSelectedEvent) {
    const tagsControl = this.form.get('tags');
    tagsControl?.setValue([...new Set([...(tagsControl?.value || []), option.viewValue.trim()])]);
    tagsControl?.updateValueAndValidity();
    this.tagCtrl.setValue(null);
    this.tagInput.nativeElement.value = '';
  }

  remove(tag: string) {
    this.tagsControl()?.value?.splice(this.tagsControl()?.value?.indexOf(tag) || 0, 1);
    this.tagsControl()?.updateValueAndValidity();
  }

  save() {
    const typeControlVal = this.form.get('type')?.value;
    if (this.isFormInvalid()) {
      this.form.markAllAsTouched();
      this.openSnackbarWith('Please select all required fields');
      return;
    }

    if (typeControlVal === 'Pdf' && this.fileUploadTouched && this.thumbnailUploadTouched) {
      this.validateFile();
    }

    this.saveTutorial();
  }

  isFormInvalid(): boolean {
    const typeControlVal = this.form.get('type')?.value;
    return this.form.invalid || (typeControlVal === 'Pdf' && !this.fileName) || !this.thumbnailName;
  }

  close() {
    this.dialogRef.close();
  }

  handleFileInput(files: FileList | EventTarget | null) {
    if (files) {
      if ((files as HTMLInputElement).files) {
        const inputFiles = (files as HTMLInputElement).files;
        this.file = inputFiles ? inputFiles[0] : null;
      } else {
        this.file = (files as FileList)[0];
      }
      this.fileUploadTouched = true;
      this.fileName = this.file!.name;
    }
  }

  handleThumbnailInput(files: FileList | EventTarget | null) {
    if (files) {
      if ((files as HTMLInputElement).files) {
        const inputFiles = (files as HTMLInputElement).files;
        this.thumbnail = inputFiles ? inputFiles[0] : null;
      } else {
        this.thumbnail = (files as FileList)[0];
      }
      this.thumbnailUploadTouched = true;
      this.thumbnailName = this.thumbnail!.name;
    }
  }

  private handleTypeChange() {
    const urlControl = this.form.get('url');
    this.form
      .get('type')
      ?.valueChanges.pipe(
        tap(val => {
          if (val === 'Video') {
            urlControl?.setValidators(Validators.required);
            this.clearFileUpload();
          } else {
            urlControl?.setValidators([]);
            urlControl?.setValue('');
          }
          urlControl?.updateValueAndValidity();
        })
      )
      .subscribe();
  }

  private clearFileUpload() {
    this.file = null;
    this.fileName = '';
  }

  private openSnackbarWith(message: string, type: 'error' | 'success' = 'error') {
    this.snackBar.open(message, 'x', {
      verticalPosition: 'top',
      duration: 3000,
      panelClass: [type === 'error' ? 'snackbar-red' : 'snackbar-green']
    });
  }

  private saveTutorial() {
    const saved: TutorialSaved = 'Saved';
    const formValue: FormTutorial = this.form.getRawValue();
    const data = {
      form: formValue,
      file: this.file,
      thumbnail: this.thumbnail
    };
    const saveMethod = this.data.editMode
      ? this.tutorialsService.update(this.data.tutorial?.id!, data)
      : this.tutorialsService.create(data);
    this.isSaving = true;
    this.subscription.add(
      saveMethod
        .pipe(
          tap(() => {
            this.openSnackbarWith('The tutorial was saved successfully', 'success');
            this.isSaving = false;
            this.dialogRef.close(saved);
          }),
          catchError(error => {
            console.error(error);
            this.openSnackbarWith('There was an error. Please try again.');
            this.isSaving = false;
            return NEVER;
          })
        )
        .subscribe()
    );
  }

  private validateFile() {
    if (this.stringHelperService.notAllowedSpecialChars(this.fileName)) {
      this.openSnackbarWith('File name cannot contain some special characters. Please rename the file.');
      return;
    }

    if (
      (this.file && this.file.size > this.fileMaxSize) ||
      (this.thumbnail && this.thumbnail.size > this.fileMaxSize)
    ) {
      this.openSnackbarWith('Max file size: 25MB');
      return;
    }
  }

  private isTagNameValid(text: string) {
    const pattern = /^[a-zA-Z0-9]*$/;
    return pattern.test(text);
  }

  private setFormValues() {
    if (this.data.editMode && this.data.tutorial) {
      this.form.patchValue(this.data.tutorial);
      this.fileName = this.data.tutorial?.fileName || '';
      this.thumbnailName = 'Thumbnail uploaded';
    }
  }

  private filterTags(allTags: string[], selectedTags: string) {
    return allTags.filter(tag => tag.toLowerCase().startsWith(selectedTags.toLowerCase()));
  }

  private tagsControl() {
    return this.form.get('tags');
  }
}
